import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, X, Loader2, Building2, Mail, Phone, MapPin, Users2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-head text-lg font-bold text-slate-900">{title}</h3>
              <button onClick={onClose} data-testid="business-modal-close" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6">{children}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">{label}{required && <span className="text-rose-500"> *</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} required={required}
        className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
    </div>
  );
}

const empty = { name: "", contact_email: "", phone: "", address: "" };

export default function Businesses() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const canDelete = me?.role === "super_admin" || me?.can_delete;
  const canEdit = me?.role === "super_admin" || me?.can_edit;
  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const { data: businesses = [], isLoading } = useQuery({ queryKey: ["businesses"], queryFn: () => api.get("/admin/businesses") });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["businesses"] });

  const saveMut = useMutation({
    mutationFn: (body) => (editingId ? api.put(`/admin/businesses/${editingId}`, body) : api.post("/admin/businesses", body)),
    onSuccess: () => { toast.success(editingId ? "Business updated" : "Business created"); setModal(false); invalidate(); },
    onError: (e) => setErr(apiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/businesses/${id}`),
    onSuccess: () => { toast.success("Business deleted"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const openAdd = () => { setEditingId(null); setForm(empty); setErr(""); setModal(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({ name: b.name || "", contact_email: b.contact_email || "", phone: b.phone || "", address: b.address || "" });
    setErr("");
    setModal(true);
  };
  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("Business name is required."); return; }
    saveMut.mutate({ name: form.name.trim(), contact_email: form.contact_email || null, phone: form.phone || null, address: form.address || null });
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="businesses-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-2xl font-bold text-slate-900">Businesses</h2>
          <p className="text-sm text-slate-500 mt-0.5">Manage the companies your staff belong to.</p>
        </div>
        <button onClick={openAdd} data-testid="business-create-btn" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition">
          <Plus className="h-4 w-4" /> Add Business
        </button>
      </div>

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
      ) : businesses.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="businesses-empty">
          <Building2 className="h-9 w-9 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No businesses yet. Add your first company to start assigning staff.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {businesses.map((b) => (
            <div key={b.id} data-testid={`business-card-${b.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-indigo-50 text-indigo-600 flex-shrink-0"><Building2 className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{b.name}</p>
                    {b.business_number && <p className="text-xs font-mono font-bold text-[#FF5A4D]" data-testid={`business-number-${b.id}`}>No. {b.business_number}</p>}
                    <p className="text-xs text-slate-400 flex items-center gap-1"><Users2 className="h-3 w-3" /> {b.employee_count} employee{b.employee_count === 1 ? "" : "s"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => openEdit(b)}
                      data-testid={`business-edit-${b.id}`}
                      className="text-slate-300 hover:text-indigo-600 transition"
                      title="Edit business"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => { if (window.confirm(`Delete "${b.name}"? This cannot be undone.`)) delMut.mutate(b.id); }}
                      data-testid={`business-delete-${b.id}`}
                      className="text-slate-300 hover:text-rose-500 transition"
                      title="Delete business"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-500">
                {b.contact_email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-400" /> {b.contact_email}</p>}
                {b.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {b.phone}</p>}
                {b.address && <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" /> <span className="line-clamp-2">{b.address}</span></p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingId ? "Edit Business" : "Add Business"}>
        <form onSubmit={submit} className="space-y-4" data-testid="business-form">
          <Field label="Business Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="business-name" placeholder="e.g. MRP Contracting Co." required />
          <Field label="Contact Email" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="business-email" placeholder="Optional" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="business-phone" placeholder="Optional" />
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Address</label>
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="business-address" rows={2} placeholder="Optional"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 resize-none" />
          </div>
          {err && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saveMut.isPending} data-testid="business-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
              {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {editingId ? "Save Changes" : "Create Business"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
